/**
 * Settings Screen
 *
 * App preferences and account settings.
 */

import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Modal,
  Platform,
  Switch,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useRouter, usePathname } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { HouseholdRow } from '../../src/components/household/HouseholdRow';
import { RootState, AppDispatch, store } from '../../src/redux/store';
import { Colors, AppTypography, Magazine, Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius, PaprikaShadows } from '../../src/styles/theme';
import { ConfirmationModal } from '../../src/components/common/ConfirmationModal';
import { logout } from '../../src/redux/authSlice';
import { clearInventoryAndSync, selectActiveInventory } from '../../src/redux/inventorySlice';
import { useAppDispatch } from '../../src/redux/hooks';
import {
  changeAppLanguage,
} from '../../src/redux/languageSlice';
import { pushAllWeeksToCloud } from '../../src/redux/mealPlanSlice';
import { setDailyDigestOptIn, selectDailyDigestOptIn, selectTasteAxes } from '../../src/redux/userPreferencesSlice';
import type { Axis, TasteAxes } from '../../src/services/tasteAxes';
import MealScheduleSettings from '../../src/components/settings/MealScheduleSettings';
import { LabsSection } from '../../src/components/settings/LabsSection';
import { FamilySizeSelector } from '../../src/components/common/FamilySizeSelector';
import {
  BugReportModal,
  gatherContext,
  getBufferedLogs,
} from '@youbbi/expo-bug-reporter';
import { getAuthHeaders } from '../../src/services/apiClient';
import { FeedbackSheet } from '../../src/components/feedback/FeedbackSheet';

/** Literal keys so the orphan-key guard sees every one of them. */
const AXIS_SUMMARY: { [A in Axis]: Record<TasteAxes[A], string> } = {
  base: { protein: 'onboarding.reading.chip.base.protein', vegetal: 'onboarding.reading.chip.base.vegetal' },
  time: { quick: 'onboarding.reading.chip.time.quick', long: 'onboarding.reading.chip.time.long' },
  heat: { bold: 'onboarding.reading.chip.heat.bold', mild: 'onboarding.reading.chip.heat.mild' },
};

const SettingsScreen: React.FC = () => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const languageDispatch = useAppDispatch();
  const { currentLanguage, isChanging: isChangingLanguage } = useSelector(
    (state: RootState) => state.language || { currentLanguage: 'en' as const, isChanging: false, error: null }
  );

  const pathname = usePathname();
  const authState = useSelector((state: RootState) => state.auth);

  const dietaryRestrictions = useSelector((state: RootState) => state.userPreferences?.dietaryRestrictions ?? []);
  const dislikedIngredients = useSelector((state: RootState) => state.userPreferences?.dislikedIngredients ?? []);
  const tasteAxes = useSelector(selectTasteAxes);
  const excludedStaplesCount = useSelector((state: RootState) => state.userPreferences?.excludedStaples?.length ?? 0);
  const dailyDigestOptIn = useSelector(selectDailyDigestOptIn);
  const user = authState?.user;

  const IMPORT_EMAIL = 'recipes@duchef.app';
  const [emailCopied, setEmailCopied] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // Bug report state
  const [bugReportVisible, setBugReportVisible] = useState(false);
  const [bugContext, setBugContext] = useState<Record<string, unknown>>({});

  // Feedback sheet — the floating bubble that used to open this was removed;
  // Settings is now its only entry point.
  const [feedbackVisible, setFeedbackVisible] = useState(false);

  const handleReportProblem = useCallback(() => {
    const contextData = gatherContext({
      getAppState: () => {
        const state = store.getState();
        return {
          mealPlan: state.mealPlan,
          userPreferences: state.userPreferences,
        };
      },
      getRoute: () => pathname,
    });
    const consoleLogs = getBufferedLogs();
    setBugContext({
      ...contextData,
      consoleLogs,
      source: 'settings',
      user: user ? { email: user.email, uid: user.uid } : 'guest',
    });
    setBugReportVisible(true);
  }, [pathname, user]);

  const handleBugReportClose = useCallback(() => {
    setBugReportVisible(false);
    setBugContext({});
  }, []);

  const handleCopyEmail = useCallback(async () => {
    await Clipboard.setStringAsync(IMPORT_EMAIL);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  }, [IMPORT_EMAIL]);

  const handleLanguageChange = async (language: 'en' | 'fr') => {
    if (language === currentLanguage) return;

    try {
      await languageDispatch(changeAppLanguage(language)).unwrap();
      setModalMessage(
        t('language.currentLanguage') +
          ': ' +
          (language === 'fr' ? t('language.french') : t('language.english'))
      );
      setShowModal(true);
    } catch (error) {
      console.error('[settings] language change failed', error);
      setModalMessage(t('errors.languageChangeFailed'));
      setShowModal(true);
    }
  };

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showClearPantryConfirm, setShowClearPantryConfirm] = useState(false);
  // Counted, not guessed: the confirmation names how many rows are about to go,
  // and the button is inert when there is nothing to clear.
  const activePantryCount = useSelector(selectActiveInventory).length;
  const [isClearingPantry, setIsClearingPantry] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const handleForceSync = useCallback(async () => {
    if (!user?.uid || isSyncing) return;
    setIsSyncing(true);
    setSyncDone(false);
    try {
      await dispatch(pushAllWeeksToCloud(user.uid)).unwrap();
      setSyncDone(true);
      setTimeout(() => setSyncDone(false), 3000);
    } catch (error) {
      console.error('[settings] meal plan sync failed', error);
      setModalMessage(t('settings.syncFailed'));
      setShowModal(true);
    } finally {
      setIsSyncing(false);
    }
  }, [user?.uid, isSyncing, dispatch]);

  /**
   * Clear the pantry, cloud first.
   *
   * clearInventoryAndSync has existed since the pantry did, with no way to
   * reach it. Deleting the rows server-side alone does not work: every client
   * keeps its own persisted copy and re-uploads it on the next session, so the
   * items come straight back. This is the only path that clears both.
   */
  const handleClearPantry = useCallback(async () => {
    setShowClearPantryConfirm(false);
    setIsClearingPantry(true);
    try {
      await dispatch(clearInventoryAndSync({ userId: user?.uid })).unwrap();
      setModalMessage(t('pantry.clear.done'));
    } catch (error) {
      // The thunk clears local state even when the cloud write fails, so the
      // pantry looks empty either way — say so rather than claiming success.
      console.error('[settings] pantry clear did not reach the cloud', error);
      setModalMessage(t('pantry.clear.localOnly'));
    } finally {
      setIsClearingPantry(false);
      setShowModal(true);
    }
  }, [dispatch, user?.uid, t]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>{t('navigation.settings')}</Text>
        <Text style={styles.subtitle}>{t('settings.subtitle')}</Text>
      </View>

      {/* Settings Content */}
      <ScrollView style={styles.settingsContainer}>
        {/* Account Section */}
        <View style={styles.settingsSection}>
          <Text style={styles.settingsSectionTitle}>
            {t('profile.accountInformation')}
          </Text>
          {user ? (
            <>
              <View style={styles.accountInfo}>
                <Text style={styles.accountLabel}>{t('auth.email')}</Text>
                <Text style={styles.accountValue}>{user.email}</Text>
              </View>
              <HouseholdRow onPress={() => router.push('/share-partner')} />
              <TouchableOpacity
                style={styles.logoutButton}
                onPress={handleLogout}
              >
                <Text style={styles.logoutButtonText}>{t('auth.logout')}</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <Text style={styles.guestModeText}>
                {t('auth.guestMode')} - {t('auth.signInToSync')}
              </Text>
              <TouchableOpacity
                style={styles.signInButton}
                onPress={() => router.push('/auth')}
              >
                <Text style={styles.signInButtonText}>{t('auth.signIn')}</Text>
              </TouchableOpacity>
            </>
          )}
        </View>

        {/* Pantry */}
        <View style={styles.settingsSection}>
          <Text style={styles.settingsSectionTitle}>{t('settings.pantryTitle')}</Text>
          <Text style={styles.settingsSectionBody}>
            {t('pantry.clear.explainer')}
          </Text>
          <TouchableOpacity
            style={[styles.logoutButton, isClearingPantry && styles.buttonBusy]}
            onPress={() => setShowClearPantryConfirm(true)}
            disabled={isClearingPantry || activePantryCount === 0}
            accessibilityRole="button"
            accessibilityState={{ disabled: isClearingPantry || activePantryCount === 0 }}
          >
            <Text style={styles.logoutButtonText}>
              {isClearingPantry
                ? t('pantry.clear.busy')
                : t('pantry.clear.button', { count: activePantryCount })}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Email Import */}
        <View style={styles.settingsSection}>
          <Text style={styles.settingsSectionTitle}>
            {t('settings.emailImportTitle')}
          </Text>
          <Text style={styles.settingsDescription}>
            {t('settings.emailImportDescription')}
          </Text>
          <View style={styles.emailImportBox}>
            <Text style={styles.emailImportAddress} numberOfLines={1} adjustsFontSizeToFit>
              {IMPORT_EMAIL}
            </Text>
          </View>
          <TouchableOpacity style={styles.reportButton} onPress={handleCopyEmail}>
            <Feather
              name={emailCopied ? 'check' : 'copy'}
              size={18}
              color={Paprika.accent}
              style={{ marginRight: PaprikaSpacing.sm }}
            />
            <Text style={styles.reportButtonText}>
              {emailCopied ? t('settings.emailImportCopied') : t('settings.emailImportCopy')}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Notifications */}
        {user && (
          <View style={styles.settingsSection}>
            <Text style={styles.settingsSectionTitle}>{t('settings.notifications')}</Text>
            <View style={styles.digestRow}>
              <View style={styles.digestRowText}>
                <Text style={styles.digestLabel}>{t('settings.dailyEmail')}</Text>
                <Text style={styles.settingsDescription}>{t('settings.dailyEmailDesc')}</Text>
              </View>
              <Switch
                testID="settings-daily-digest"
                value={dailyDigestOptIn}
                onValueChange={(next) => {
                  dispatch(setDailyDigestOptIn(next));
                }}
                trackColor={{ false: Paprika.border, true: Paprika.accent }}
                thumbColor={Platform.OS === 'android' ? '#ffffff' : undefined}
                ios_backgroundColor={Paprika.border}
              />
            </View>
          </View>
        )}

        {/* Language Settings */}
        <View style={styles.settingsSection}>
          <Text style={styles.settingsSectionTitle}>
            {t('language.changeLanguage')}
          </Text>
          <Text style={styles.settingsDescription}>
            {t('language.currentLanguage')}:{' '}
            {currentLanguage === 'fr'
              ? t('language.french')
              : t('language.english')}
          </Text>

          <View style={styles.languageButtons}>
            <TouchableOpacity
              style={[
                styles.languageButton,
                currentLanguage === 'en' && styles.languageButtonActive,
                isChangingLanguage && styles.languageButtonDisabled,
              ]}
              onPress={() => handleLanguageChange('en')}
              disabled={isChangingLanguage}
              testID="settings-language-en"
            >
              <Feather
                name="globe"
                size={18}
                color={currentLanguage === 'en' ? '#FFFFFF' : Paprika.textSecondary}
                style={styles.languageFlag}
              />
              <Text
                style={[
                  styles.languageButtonText,
                  currentLanguage === 'en' && styles.languageButtonTextActive,
                ]}
              >
                {t('language.english')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.languageButton,
                currentLanguage === 'fr' && styles.languageButtonActive,
                isChangingLanguage && styles.languageButtonDisabled,
              ]}
              onPress={() => handleLanguageChange('fr')}
              disabled={isChangingLanguage}
              testID="settings-language-fr"
            >
              <Feather
                name="globe"
                size={18}
                color={currentLanguage === 'fr' ? '#FFFFFF' : Paprika.textSecondary}
                style={styles.languageFlag}
              />
              <Text
                style={[
                  styles.languageButtonText,
                  currentLanguage === 'fr' && styles.languageButtonTextActive,
                ]}
              >
                {t('language.french')}
              </Text>
            </TouchableOpacity>
          </View>

          {isChangingLanguage && (
            <Text style={styles.changingText}>{t('language.changing')}</Text>
          )}
        </View>

        {/* Default Servings */}
        <View style={styles.settingsSection}>
          <FamilySizeSelector />
        </View>

        {/* Taste profile — the axes, the hard rules and what the digest learned,
            all in one place. The planner reads exactly this (cookPreferences). */}
        <View style={styles.settingsSection}>
          <Text style={styles.settingsSectionTitle}>{t('settings.myTasteTitle')}</Text>
          <TouchableOpacity
            style={styles.sharePartnerButton}
            onPress={() => router.push('/taste-profile')}
            testID="settings-taste-profile"
          >
            <Feather name="sliders" size={18} color={Paprika.accent} />
            <View style={styles.sharePartnerText}>
              <Text style={styles.sharePartnerTitle}>{t('tasteProfile.title')}</Text>
              <Text style={styles.sharePartnerSubtitle}>
                {tasteAxes
                  ? [t(AXIS_SUMMARY.base[tasteAxes.base]), t(AXIS_SUMMARY.time[tasteAxes.time]), t(AXIS_SUMMARY.heat[tasteAxes.heat])].join(' · ')
                  : t('settings.myTasteEmpty')}
              </Text>
            </View>
            <Feather name="chevron-right" size={20} color={Paprika.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* Dietary Preferences */}
        <View style={styles.settingsSection}>
          <Text style={styles.settingsSectionTitle}>{t('settings.dietaryPreferences')}</Text>
          <TouchableOpacity
            style={styles.sharePartnerButton}
            onPress={() => router.push('/preferences/diet')}
          >
            <Feather name="filter" size={18} color={Paprika.accent} />
            <View style={styles.sharePartnerText}>
              <Text style={styles.sharePartnerTitle}>
                {dietaryRestrictions.length > 0
                  ? dietaryRestrictions.join(', ')
                  : t('settings.noRestrictions')}
              </Text>
              <Text style={styles.sharePartnerSubtitle}>
                {dislikedIngredients.length > 0
                  ? t('settings.excludedIngredientCount', { count: dislikedIngredients.length })
                  : t('settings.noExcludedIngredients')}
              </Text>
            </View>
            <Feather name="chevron-right" size={20} color={Paprika.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* Kitchen Staples */}
        <View style={styles.settingsSection}>
          <Text style={styles.settingsSectionTitle}>{t('settings.kitchenStaples')}</Text>
          <TouchableOpacity
            style={styles.sharePartnerButton}
            onPress={() => router.push('/preferences/staples')}
          >
            <Feather name="award" size={18} color={Paprika.accent} />
            <View style={styles.sharePartnerText}>
              <Text style={styles.sharePartnerTitle}>
                {t('settings.kitchenStaples')}
              </Text>
              <Text style={styles.sharePartnerSubtitle}>
                {excludedStaplesCount > 0
                  ? t('settings.excludedStapleCount', { count: excludedStaplesCount })
                  : t('settings.noExcludedStaples')}
              </Text>
            </View>
            <Feather name="chevron-right" size={20} color={Paprika.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* Meal Schedule Preferences */}
        <View style={styles.settingsSection}>
          <MealScheduleSettings />
        </View>

        {/* Help & Feedback */}
        <View style={styles.settingsSection}>
          <Text style={styles.settingsSectionTitle}>
            {t('settings.helpFeedback')}
          </Text>
          <Text style={styles.settingsDescription}>
            {t('settings.reportProblemDescription')}
          </Text>
          <TouchableOpacity
            style={styles.reportButton}
            onPress={handleReportProblem}
          >
            <Feather name="message-circle" size={18} color={Paprika.accent} style={{ marginRight: PaprikaSpacing.sm }} />
            <Text style={styles.reportButtonText}>{t('settings.reportProblem')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.reportButton, { marginTop: PaprikaSpacing.sm }]}
            onPress={() => setFeedbackVisible(true)}
          >
            <Feather name="star" size={18} color={Paprika.accent} style={{ marginRight: PaprikaSpacing.sm }} />
            <Text style={styles.reportButtonText}>{t('feedback.sheetTitle')}</Text>
          </TouchableOpacity>
          {user && (
            <TouchableOpacity
              style={[styles.reportButton, { marginTop: PaprikaSpacing.sm, opacity: isSyncing ? 0.6 : 1 }]}
              onPress={handleForceSync}
              disabled={isSyncing}
            >
              <Feather
                name={syncDone ? 'check' : 'refresh-cw'}
                size={18}
                color={Paprika.accent}
                style={{ marginRight: PaprikaSpacing.sm }}
              />
              <Text style={styles.reportButtonText}>
                {isSyncing ? t('settings.syncing') : syncDone ? t('settings.syncComplete') : t('settings.forceSync')}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Labs — renders nothing unless the account is an admin */}
        <LabsSection />
      </ScrollView>

      {/* Settings Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showModal}
        onRequestClose={() => setShowModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalMessage}>{modalMessage}</Text>
            <TouchableOpacity
              style={[styles.modalButton, styles.confirmButton]}
              onPress={() => setShowModal(false)}
            >
              <Text style={styles.confirmButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <FeedbackSheet
        visible={feedbackVisible}
        currentScreen={pathname}
        onClose={() => setFeedbackVisible(false)}
      />

      {/* Bug Report Modal */}
      <BugReportModal
        visible={bugReportVisible}
        screenshot={null}
        webhookUrl="/api/bug-report"
        context={bugContext}
        projectName="paprika"
        onClose={handleBugReportClose}
        getHeaders={getAuthHeaders}
      />

      {/* Clear Pantry Confirmation */}
      <ConfirmationModal
        visible={showClearPantryConfirm}
        title={t('pantry.clear.confirmTitle')}
        message={t('pantry.clear.confirmBody', { count: activePantryCount })}
        confirmLabel={t('pantry.clear.confirmAction')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={handleClearPantry}
        onCancel={() => setShowClearPantryConfirm(false)}
      />

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        visible={showLogoutConfirm}
        title={t('auth.logout')}
        message={t('auth.logoutConfirmation')}
        confirmLabel={t('auth.logout')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => {
          dispatch(logout());
          setShowLogoutConfirm(false);
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'ios' ? 60 : Platform.OS === 'web' ? 16 : 40,
    paddingBottom: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  title: {
    ...AppTypography.displayMedium,
    color: Magazine.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    ...AppTypography.bodySmall,
    color: Magazine.textSecondary,
    marginBottom: 16,
  },
  // Settings styles - Paprika Design System
  settingsContainer: {
    flex: 1,
    backgroundColor: Paprika.background,
    padding: PaprikaSpacing.lg,
    paddingBottom: Platform.OS === 'web' ? 100 : 40,
  },
  settingsSection: {
    width: '100%',
    marginBottom: PaprikaSpacing.md,
    padding: PaprikaSpacing.lg,
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.border,
    ...PaprikaShadows.card,
  },
  settingsSectionTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.md,
  },
  settingsDescription: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.md,
  },
  emailImportBox: {
    backgroundColor: Paprika.background,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.md,
  },
  digestRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  digestRowText: {
    flex: 1,
    paddingRight: PaprikaSpacing.md,
  },
  digestLabel: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
  },
  emailImportAddress: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },
  accountInfo: {
    marginBottom: PaprikaSpacing.sm,
  },
  accountLabel: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.xs,
  },
  accountValue: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textPrimary,
  },
  guestModeText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.sm,
  },
  signInButton: {
    backgroundColor: Paprika.accent,
    paddingVertical: 16,
    paddingHorizontal: PaprikaSpacing.xl,
    borderRadius: PaprikaRadius.xl,
    marginTop: PaprikaSpacing.lg,
    alignItems: 'center',
    alignSelf: 'center',
    minWidth: Platform.OS === 'web' ? 200 : 180,
  },
  signInButtonText: {
    ...PaprikaTypography.labelLarge,
    fontSize: 16,
    color: '#FFFFFF',
  },
  sharePartnerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Paprika.background,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.lg,
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    marginTop: PaprikaSpacing.md,
    gap: PaprikaSpacing.md,
  },
  sharePartnerText: {
    flex: 1,
  },
  sharePartnerTitle: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  sharePartnerSubtitle: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  settingsSectionBody: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.sm,
  },
  buttonBusy: {
    opacity: 0.55,
  },
  logoutButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: Paprika.destructive,
    borderRadius: PaprikaRadius.xl,
    paddingVertical: 16,
    paddingHorizontal: PaprikaSpacing.xl,
    alignItems: 'center',
    marginTop: PaprikaSpacing.lg,
    alignSelf: 'center',
    minWidth: Platform.OS === 'web' ? 200 : 180,
  },
  logoutButtonText: {
    ...PaprikaTypography.labelLarge,
    fontSize: 16,
    color: Paprika.destructive,
  },
  languageButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: PaprikaSpacing.md,
  },
  languageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.lg,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.surfaceLight,
    borderWidth: 1,
    borderColor: Paprika.border,
    minWidth: Platform.OS === 'web' ? 140 : 120,
  },
  languageButtonActive: {
    backgroundColor: Paprika.accent,
    borderColor: Paprika.accent,
  },
  languageButtonDisabled: {
    opacity: 0.5,
  },
  languageFlag: {
    marginRight: PaprikaSpacing.sm,
  },
  languageButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  languageButtonTextActive: {
    color: '#FFFFFF',
  },
  changingText: {
    ...PaprikaTypography.bodySmall,
    textAlign: 'center',
    color: Paprika.textMuted,
    marginTop: PaprikaSpacing.sm,
    fontStyle: 'italic',
  },
  reportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: Paprika.accent,
    borderRadius: PaprikaRadius.xl,
    paddingVertical: 14,
    paddingHorizontal: PaprikaSpacing.xl,
    marginTop: PaprikaSpacing.sm,
    alignSelf: 'center',
    minWidth: Platform.OS === 'web' ? 220 : 200,
  },
  reportButtonText: {
    ...PaprikaTypography.labelLarge,
    fontSize: 16,
    color: Paprika.accent,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    margin: 20,
    minWidth: 280,
    maxWidth: 340,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 8,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 16,
    color: '#666',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginHorizontal: 6,
  },
  cancelButton: {
    backgroundColor: '#f0f0f0',
  },
  confirmButton: {
    backgroundColor: Colors.primary,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
});

export default SettingsScreen;
